import React from "react";
//#region types
import {
  AutoCompleteExtraProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
} from "models/propTypes/genericFormProps";
import { RevenueReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormEnums } from "models/enums";
import { FileConstants, FormFieldConstants } from "utils/constants";
import { RevenueReportingFormProps } from "models/propTypes/revenueReportingPropTypes";
//#endregion
//#region components
import { GenericForm } from "components/common";
import RevenueReportingDifference from "./revenueReportingDifference";
import RevenueReportingAmountRow from "./revenueReportingAmountRow";
import RevenueReportingSelectPaymentInfo from "./revenueReportingSelectPaymentInfo";
import { TagsSection } from "components/common/dimensions/tagsSection";
//#endregion
import { MaxAllowedDifference } from "./revenueReportingFormConstants";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const RevenueReportingCreateForm: React.ForwardRefRenderFunction<FormHandles, RevenueReportingFormProps> = (
  props,
  ref
) => {
  const { itemChangeHandler, templateItems, showTemplateSelector } = props;

  const amountRowFields = {
    description: {
      label: "",
      type: FormEnums.FormFieldType.TEXT,
      readonly: true,
      widthColumnCount: 6,
    },
    amount: {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormFieldType.CURRENCY,
      placeholder: TranslationKeys.Common.Amount,
      widthColumnCount: 6,
    },
  } as FormFieldDefinitionType<RevenueReportingFormTypes.RevenueReportItem>;

  const tableExtraProps = {
    hideActions: true,
    isCompactTable: true,
    idFieldKey: FormFieldConstants.RevenueReporting.RevenueReportItem.TemplateItemId,
  } as EditableTableFieldProps<RevenueReportingFormTypes.RevenueReportItem>;

  const getTagRowId = (item: RevenueReportingFormTypes.RevenueReportItem, index: number) =>
    item.templateItemId ?? `${index}`;

  return (
    <GenericForm<RevenueReportingFormTypes.Create>
      formRef={ref}
      {...props}
      fields={[
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          label: TranslationKeys.Common.Attach,
          descriptionText: TranslationKeys.RevenueReporting.AttachmentsHelpText,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          innerFields: {
            filesToUpload: {
              label: " ",
              type: FormEnums.FormFieldType.FILEUPLOADER,
              extraProps: {
                fileUploaderType: "full",
                filePreviewElementId: FileConstants.DialogFilePreviewPortalId,
              },
            },
            date: {
              label: TranslationKeys.RevenueReporting.RevenueDate,
              type: FormEnums.FormFieldType.DATE,
              extraProps: {
                maxDate: new Date(),
              },
            },
            template: {
              label: TranslationKeys.RevenueReporting.PaymentMethodSelect,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              placeholder: TranslationKeys.RevenueReporting.PaymentMethodSelect,
              hidden: !showTemplateSelector,
              extraProps: {
                referenceFieldName: FormFieldConstants.RevenueReporting.Create.TemplateId,
                items: templateItems,
                onItemSelect: props.templateChangeHandler,
                onItemClear: props.templateClearHandler,
              } as AutoCompleteExtraProps<RevenueReportingFormTypes.Create>,
            },
          },
        },
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: RevenueReportingFormTypes.Create) => !showTemplateSelector || !!values.salesItems?.length,
          innerFields: {
            selectPaymentInfo: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingSelectPaymentInfo />,
            },
          } as FormFieldDefinitionType<RevenueReportingFormTypes.Create & { selectPaymentInfo?: string }>,
        },
        {
          label: TranslationKeys.Common.Sale,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: RevenueReportingFormTypes.Create) => !values.salesItems?.length,
          innerFields: {
            salesItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              innerFields: amountRowFields,
              extraProps: tableExtraProps,
            },
            salesTotalAmount: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingAmountRow labelKey={TranslationKeys.Common.Total} />,
            },
          },
        },
        {
          label: TranslationKeys.Common.PaymentMethod,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: RevenueReportingFormTypes.Create) => !values.paymentItems?.length,
          innerFields: {
            paymentItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              innerFields: amountRowFields,
              extraProps: tableExtraProps,
            },
            paymentsTotalAmount: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingAmountRow labelKey={TranslationKeys.Common.Total} />,
            },
          },
        },
        {
          label: TranslationKeys.RevenueReporting.Other,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: RevenueReportingFormTypes.Create) => !values.otherItems?.length,
          innerFields: {
            otherItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              innerFields: amountRowFields,
              extraProps: tableExtraProps,
            },
            othersTotalAmount: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingAmountRow labelKey={TranslationKeys.Common.Total} />,
            },
          },
        },
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: RevenueReportingFormTypes.Create) =>
            !values.salesItems?.length || Math.abs(values.total ?? 0) <= MaxAllowedDifference,
          innerFields: {
            total: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingDifference />,
            },
          },
        },
        {
          label: TranslationKeys.Common.Tags,
          descriptionText: TranslationKeys.RevenueReporting.TagsHelpText,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: RevenueReportingFormTypes.Create) => !values.salesItems?.length,
          innerFields: {
            dimensions: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: (
                <TagsSection<RevenueReportingFormTypes.RevenueReportItem, RevenueReportingFormTypes.Create>
                  getRowId={getTagRowId}
                  otherHeadingKey={TranslationKeys.RevenueReporting.Other}
                  translateDimensionLabels
                />
              ),
            },
          },
        },
      ]}
      hideSuccess
      formGridLayout={{ message: false }}
    />
  );
};

export default React.forwardRef(RevenueReportingCreateForm);
